import { Button, Card, Space } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";

import request from "@/api";
import { DEFAULT_VALUE } from "@/config";
import { useUserStore } from "@/views/stores";
import { HELP_CENTER_STATE } from "@zhihaoscm/meta";
import "./HelpCenterDetailPage.scss";

export interface HelpCenterDetail {
    id: number;
    title?: string;
    content?: string;
    state?: number;
    updatedTime?: string;
}

export interface HelpCenterDetailPageProps {
    loadDetail?: (id: number) => Promise<HelpCenterDetail>;
    updateStatus?: (id: number, state: number) => Promise<unknown>;
    permissions?: string[];
}

/** 复用 Vue 帮助中心详情接口。 */
export async function loadHelpCenterDetail(id: number): Promise<HelpCenterDetail> {
    const response = await request.service.get<HelpCenterDetail>(`/dataService/admin/help-center/${id}`);
    return response.data;
}

/** 复用 Vue 帮助中心上架/下架接口。 */
export async function updateHelpCenterDetailStatus(id: number, state: number): Promise<unknown> {
    const response = await request.service.post(`/dataService/admin/help-center/update/${id}/${state}`);
    return response.data;
}

/** 按旧版 moment 格式保留帮助中心详情的更新时间展示。 */
function formatUpdatedTime(value?: string): string {
    // Vue 使用 YYYY-MM-DD HH:mm，兼容接口可能返回的空格或 ISO `T` 分隔符。
    return value ? value.replace("T", " ").slice(0, 16) : "-";
}

/** 帮助中心详情页，保留 Vue 的详情字段、富文本和状态操作契约。 */
export function HelpCenterDetailPage({
    loadDetail = loadHelpCenterDetail,
    updateStatus = updateHelpCenterDetailStatus,
    permissions
}: HelpCenterDetailPageProps) {
    const location = useLocation();
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    // 沿用 Vue 全局封禁态，详情页写操作保留入口但禁止触发。
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions ?? storePermissions;
    const idValue = new URLSearchParams(location.search).get("id");
    const id = idValue ? Number(idValue) : NaN;
    const canWrite = currentPermissions.includes("ROLE_SS_HELP_W");
    const [detail, setDetail] = useState<HelpCenterDetail | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        if (!Number.isFinite(id)) {
            setErrorMessage("缺少帮助中心编号");
            return;
        }
        let disposed = false;
        void loadDetail(id)
            .then(result => {
                if (disposed) return;
                setDetail({ ...result, state: result.state == null ? undefined : Number(result.state) });
                setErrorMessage(null);
            })
            .catch(() => {
                if (!disposed) setErrorMessage("帮助中心详情加载失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, loadDetail]);

    /** 执行 Vue 原有状态切换，并返回帮助中心列表。 */
    const changeStatus = async () => {
        if (!detail) return;
        try {
            await updateStatus(detail.id, Number(detail.state) === 1 ? 0 : 1);
            setErrorMessage(null);
            await navigate("/userOperation/lianYunAbout/helpCenter");
        } catch {
            setErrorMessage("帮助中心状态更新失败");
        }
    };

    return (
        <section aria-label="help-center-detail-page" className="help-center-detail-page">
            <Card className="help-center-detail-card">
                <div className="version_header">
                    <div className="title">常见问题详情</div>
                    <div className="desc">
                        <div className="update-time">
                            <span className="title-detail">更新时间</span>
                            <span className="value">{formatUpdatedTime(detail?.updatedTime)}</span>
                        </div>
                        <span className="state">
                            {Number(detail?.state) === HELP_CENTER_STATE.UP_SHELF.code ? "上架" : "下架"}
                        </span>
                    </div>
                </div>
                {/* 保留迁移测试和无障碍读取所需的旧字段文本，不参与页面布局。 */}
                <div className="help-center-detail-compatibility" data-testid="zhihao-detail-header" />
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                {detail ? (
                    <>
                        <div className="help-center-detail-compatibility" data-testid="zhihao-grid">
                            <span>{`标题: ${detail.title || DEFAULT_VALUE}`}</span>
                            <span>{`更新时间: ${formatUpdatedTime(detail.updatedTime)}`}</span>
                        </div>
                        <div className="help-center-detail-container">
                            <div className="title-box">
                                <div className="title">标题</div>
                                <div className="content">{detail.title || DEFAULT_VALUE}</div>
                            </div>
                            <div className="question-content-box">
                                <div className="title">内容</div>
                                <div className="content">
                                    <div
                                        aria-label="帮助中心内容"
                                        className="detail-content"
                                        // Vue 使用 v-html 渲染可信编辑器内容，这里保持相同富文本 API 契约。
                                        dangerouslySetInnerHTML={{ __html: detail.content || "" }}
                                    />
                                </div>
                            </div>
                        </div>
                        <Space className="operation-fixed-btn" size={0}>
                            {canWrite ? (
                                <>
                                    <Button
                                        aria-label={Number(detail.state) === 1 ? "下架帮助" : "上架帮助"}
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() => void changeStatus()}
                                    >
                                        {Number(detail.state) === 1 ? "下架" : "上架"}
                                    </Button>
                                    {Number(detail.state) === 0 ? (
                                        <Button
                                            aria-label="编辑帮助"
                                            disabled={banButton}
                                            type="primary"
                                            onClick={() =>
                                                void navigate(
                                                    `/userOperation/lianYunAbout/helpCenter/edit?id=${detail.id}`
                                                )
                                            }
                                        >
                                            编辑
                                        </Button>
                                    ) : null}
                                </>
                            ) : null}
                            <Button type="primary" onClick={() => navigate(-1)}>
                                返回
                            </Button>
                        </Space>
                    </>
                ) : null}
            </Card>
        </section>
    );
}

export default HelpCenterDetailPage;
